import { useState, useEffect } from 'react';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import Table from 'react-bootstrap/Table';
import Navbar from "../components/Navbar";
import * as client from '../api/client';
import '../styles/styles.css';

function ProductPage() {
    const [products, setProducts] = useState([]);
    const [originalProducts, setOriginalProducts] = useState([]);
    const [newProduct, setNewProduct] = useState({
        name: '',
        laborCosts: null,
        price: null
    });

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [productsMarkedForDeletion, setProductsMarkedForDeletion] = useState([]);

    const handleCloseModal = () => {
        setShowModal(false);
        setNewProduct({ name: '', laborCosts: null, price: null });
    };



    useEffect(() => {
        fetchProducts();
    }, []);

    const fetchProducts = async () => {
        setIsLoading(true);
        try {
            const products = await client.getAllProducts();
            setProducts(products);
            setOriginalProducts(products);
        } catch (err) {
            setError('Failed to fetch products');
        } finally {
            setIsLoading(false);
        }
    };

    const numericFields = ["laborCosts", "price"];

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewProduct(prev => ({
            ...prev,
            [name]: numericFields.includes(name)
                ? (value === "" ? null : parseFloat(value))
                : value
        }));
    };

    const handleEditInputChange = (e, productId) => {
        const { name, value } = e.target;
        setProducts(prev =>
            prev.map(p =>
                p.id === productId
                    ? {
                        ...p,
                        [name]: numericFields.includes(name)
                            ? (value === "" ? null : parseFloat(value))
                            : value
                    }
                    : p
            )
        );
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError(null);

        try {
            const productToSend = {
                ...newProduct,
                laborCosts: newProduct.laborCosts ?? 0,
                price: newProduct.price ?? 0
            };

            const product = await client.createProduct(productToSend);
            setProducts(prev => [...prev, product]);
            setOriginalProducts(prev => [...prev, product]);
            handleCloseModal();
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Failed to create product');
        } finally {
            setIsLoading(false);
        }
    };

    const toggleMarkForDeletion = (productId) => {
        setProductsMarkedForDeletion(prev =>
            prev.includes(productId)
                ? prev.filter(id => id !== productId)
                : [...prev, productId]
        );
    };

    const handleBulkDelete = async () => {
        if (productsMarkedForDeletion.length === 0) return;

        setIsLoading(true);
        setError(null);

        try {
            await client.deleteProducts(productsMarkedForDeletion);
            const updatedProducts = products.filter(p => !productsMarkedForDeletion.includes(p.id));
            setProducts(updatedProducts);
            setOriginalProducts(updatedProducts);
            setProductsMarkedForDeletion([]);
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Failed to delete products');
        } finally {
            setIsLoading(false);
        }
    };

    const handleBulkUpdate = async () => {
        setIsLoading(true);
        setError(null);

        try {
            const changedProducts = products.filter((product, index) => {
                const original = originalProducts[index];
                return (
                    product.name !== original.name ||
                    product.laborCosts !== original.laborCosts ||
                    product.price !== original.price
                );
            });

            if (changedProducts.length > 0) {
                const response = await client.bulkApplyProducts(changedProducts);

                const updatedProductsMap = new Map(response.map(product => [product.id, product]));
                const updatedProductsList = products.map(product =>
                    updatedProductsMap.get(product.id) || product
                );

                setProducts(updatedProductsList);
                setOriginalProducts(updatedProductsList);
            }

            setEditMode(false);
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Failed to bulk update products');
        } finally {
            setIsLoading(false);
        }
    };

    const isProductChanged = (product, index) => {
        const original = originalProducts[index];
        return original && (
            product.name !== original.name ||
            product.laborCosts !== original.laborCosts ||
            product.price !== original.price
        );
    };

    return (
        <div className="product-page-container">
            <Navbar />
            <div className="container mt-4">
                <h1 className="mb-4">Продукты</h1>

                {error && <div className="alert alert-danger mb-4">{error}</div>}

                <div className="d-flex mb-4 gap-2 flex-wrap">
                    <Button variant="outline-secondary" className="custom-outline-secondary" onClick={() => setShowModal(true)}>
                        Добавить продукт
                    </Button>
                    <Button
                        variant={editMode ? "danger" : "outline-primary"}
                        onClick={() => {
                            if (editMode) {
                                setProducts([...originalProducts]);
                                setProductsMarkedForDeletion([]);
                            }
                            setEditMode(!editMode);
                        }}
                    >
                        {editMode ? "Отменить" : "Редактировать"}
                    </Button>
                    {editMode && (
                        <>
                            <Button
                                variant="danger"
                                onClick={handleBulkDelete}
                                disabled={isLoading || productsMarkedForDeletion.length === 0}
                            >
                                {isLoading ? 'Удаление...' : `Удалить отмеченные (${productsMarkedForDeletion.length})`}
                            </Button>
                            <Button
                                variant="primary"
                                onClick={handleBulkUpdate}
                                disabled={isLoading}
                            >
                                {isLoading ? 'Сохранение...' : 'Сохранить изменения'}
                            </Button>
                        </>
                    )}
                </div>

                <Modal show={showModal} onHide={handleCloseModal}>
                    <Modal.Header closeButton>
                        <Modal.Title>Добавить новый продукт</Modal.Title>
                    </Modal.Header>
                    <Form onSubmit={handleSubmit}>
                        <Modal.Body>
                            <Form.Group className="mb-3">
                                <Form.Label>Название:</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="name"
                                    value={newProduct.name}
                                    onChange={handleInputChange}
                                    required
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Себестоимость:</Form.Label>
                                <Form.Control
                                    type="number"
                                    name="laborCosts"
                                    step="0.01"
                                    min="0"
                                    value={newProduct.laborCosts ?? ""}
                                    onChange={handleInputChange}
                                    required
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Стоимость:</Form.Label>
                                <Form.Control
                                    type="number"
                                    name="price"
                                    step="0.01"
                                    min="0"
                                    value={newProduct.price ?? ""}
                                    onChange={handleInputChange}
                                    required
                                />
                            </Form.Group>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={handleCloseModal}>
                                Отмена
                            </Button>
                            <Button variant="primary" type="submit" disabled={isLoading}>
                                {isLoading ? 'Сохранение...' : 'Сохранить'}
                            </Button>
                        </Modal.Footer>
                    </Form>
                </Modal>

                <div className="products-list">
                    {isLoading && !products.length ? (
                        <div className="text-center my-5">
                            <div className="spinner-border" role="status">
                                <span className="visually-hidden">Загрузка...</span>
                            </div>
                        </div>
                    ) : (
                        <Table striped bordered hover responsive>
                            <thead>
                                <tr>
                                    <th>Название</th>
                                    <th>Трудозатраты</th>
                                    <th>Цена</th>
                                    {editMode && <th>Действия</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {products.map((product, index) => {
                                    const isChanged = isProductChanged(product, index);
                                    const isMarkedForDeletion = productsMarkedForDeletion.includes(product.id);

                                    return (
                                        <tr
                                            key={product.id}
                                            className={`
                                                ${isChanged ? 'table-warning' : ''}
                                                ${isMarkedForDeletion ? 'table-danger' : ''}
                                            `}
                                        >
                                            <td>
                                                {editMode ? (
                                                    <Form.Control
                                                        type="text"
                                                        name="name"
                                                        value={product.name}
                                                        onChange={(e) => handleEditInputChange(e, product.id)}
                                                    />
                                                ) : (
                                                    product.name
                                                )}
                                            </td>
                                            <td>
                                                {editMode ? (
                                                    <Form.Control
                                                        type="number"
                                                        name="laborCosts"
                                                        step="0.01"
                                                        min="0"
                                                        value={product.laborCosts}
                                                        onChange={(e) => handleEditInputChange(e, product.id)}
                                                    />
                                                ) : (product.laborCosts)}
                                            </td>
                                            <td>
                                                {editMode ? (
                                                    <Form.Control
                                                        type="number"
                                                        name="price"
                                                        step="0.01"
                                                        min="0"
                                                        value={product.price}
                                                        onChange={(e) => handleEditInputChange(e, product.id)}
                                                    />
                                                ) : (product.price)}
                                            </td>
                                            {editMode && (
                                                <td>
                                                    <Button
                                                        variant={isMarkedForDeletion ? "outline-success" : "outline-danger"}
                                                        size="sm"
                                                        onClick={() => toggleMarkForDeletion(product.id)}
                                                    >
                                                        {isMarkedForDeletion ? 'Отменить удаление' : 'Удалить'}
                                                    </Button>
                                                </td>
                                            )}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    )}
                </div>
            </div>
        </div>
    );
}

export default ProductPage;